import { useState } from "react";
import { Play, Video as VideoIcon } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Video Exhibit — an editorial video module. Framed as a piece of evidence
 * inside a strategy paper, not a marketing hero.
 *
 *   \u2022 No autoplay.
 *   \u2022 Constrained width; the paper's copy remains the primary experience.
 *   \u2022 Renders a graceful placeholder state when `src` is not yet provided.
 *
 * Usage:
 *   <VideoExhibit
 *     exhibit="Exhibit A"
 *     label="Independent Product Overview"
 *     description="A third-party walkthrough explaining how HAEVN works."
 *     src={undefined}
 *   />
 */
export const VideoExhibit = ({
  exhibit,
  label,
  description,
  src,
  poster,
  testId,
}) => {
  const [playing, setPlaying] = useState(false);
  const hasSrc = Boolean(src);

  return (
    <figure
      data-testid={testId || "video-exhibit"}
      className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 sm:p-8"
    >
      <figcaption className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        {exhibit && (
          <span className="haevn-eyebrow" data-testid={`${testId || "video-exhibit"}-exhibit`}>
            {exhibit}
          </span>
        )}
        <span
          className="text-base font-medium tracking-tight text-foreground sm:text-lg"
          data-testid={`${testId || "video-exhibit"}-label`}
        >
          {label}
        </span>
      </figcaption>

      {description && (
        <p
          className="max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-[15px]"
          data-testid={`${testId || "video-exhibit"}-description`}
        >
          {description}
        </p>
      )}

      <div
        className={cn(
          "relative overflow-hidden rounded-lg border border-border bg-surface/40",
          "aspect-video"
        )}
        data-testid={`${testId || "video-exhibit"}-frame`}
      >
        {hasSrc ? (
          playing ? (
            <video
              src={src}
              poster={poster}
              controls
              preload="metadata"
              autoPlay
              playsInline
              className="h-full w-full bg-black"
              data-testid={`${testId || "video-exhibit"}-player`}
            />
          ) : (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              className={cn(
                "group absolute inset-0 flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                poster ? "bg-black/25 hover:bg-black/15" : "bg-black/70 hover:bg-black/60"
              )}
              data-testid={`${testId || "video-exhibit"}-play`}
              aria-label={`Play ${label}`}
            >
              {poster && (
                <img
                  src={poster}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}
              <span className="relative flex h-16 w-16 items-center justify-center rounded-full border border-white/40 bg-white/10 backdrop-blur transition-transform group-hover:scale-105">
                <Play className="h-6 w-6 translate-x-0.5 text-white" />
              </span>
            </button>
          )
        ) : (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-surface/60 text-muted-foreground"
            data-testid={`${testId || "video-exhibit"}-placeholder`}
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-full border border-border bg-background">
              <VideoIcon className="h-5 w-5" />
            </div>
            <div className="text-center">
              <div className="text-xs font-medium uppercase tracking-[0.14em]">
                Awaiting upload
              </div>
              <div className="mt-1 text-xs">This exhibit will appear here.</div>
            </div>
          </div>
        )}
      </div>
    </figure>
  );
};

export default VideoExhibit;
